import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthenticationService } from './authentication/authentication.service';
import { AppService } from './services/app.service';
import { UserMenuService } from 'projects/shared/src/lib/services/user-menu.service';

export const userMenuGuard: CanActivateFn = (route, state) => {
  const authS = inject(AuthenticationService);
  const router = inject(Router);
  const appS = inject(AppService);
  const userMenuS = inject(UserMenuService);

  // Allow all for superAdmin or if no menu details are loaded (yet)
  if (authS.isSuperAdmin()) return true;

  const menuDetails = userMenuS.userMenuDetails();

  // If no menu details, it might be a public page or not yet loaded properly
  // Better to allow if no details exist, but let's check if the user HAS a menu assigned
  if (!authS.user()?.userMenu) return true;

  // Wait, if they have a menu but details aren't loaded, we might need to wait?
  // rxResource should handle loading. For now, let's assume if it's assigned, we must check it.

  if (!menuDetails || menuDetails.length === 0) {
    // If it's still loading, we might need a more robust check.
    // But for a simple implementation:
    return true;
  }

  const url = state.url;

  // Set of all allowed slugs (IDs, paths, fullPaths)
  const accessibleSlugs = new Set(menuDetails.filter((d) => d.viewAccess && d.slug).map((d) => d.slug!));

  // Flatten the system menu to find matching paths
  const allMenus = appS.unfilteredSystemMenu();
  const flattenMenu = (items: any[]): any[] => {
    return items.reduce((acc, item) => {
      acc.push(item);
      if (item.children) {
        acc.push(...flattenMenu(item.children));
      }
      return acc;
    }, []);
  };

  const flatMenus = flattenMenu(allMenus);

  // Find the most specific menu item that matches the requested URL
  // We sort by length descending to match the deepest child route first
  const matchingItems = flatMenus
    .filter((item) => item.fullPath && url.includes(item.fullPath))
    .sort((a, b) => b.fullPath.length - a.fullPath.length);

  const matchedItem = matchingItems[0]; // The most specific match
  let isAccessible = false;

  if (matchedItem) {
    // Consistency Fix: Evaluate using the exact same logic as AppService
    isAccessible =
      accessibleSlugs.has(matchedItem.id!) ||
      accessibleSlugs.has(matchedItem.path!) ||
      accessibleSlugs.has(matchedItem.fullPath!);
  }

  // Fallback for routes outside the menu structure or abstract slugs
  if (!isAccessible) {
    isAccessible = Array.from(accessibleSlugs).some(
      (slug) => url.includes(slug) || slug === url || (slug.startsWith('/') && url.startsWith(slug)),
    );
  }

  if (isAccessible) return true;

  // Final check: some routes might be universal (like dashboard or profile)
  const universalRoutes = ['dashboard', 'profile', 'access-denied'];
  if (universalRoutes.some((r) => url.includes(r))) return true;

  // Redirect to Access Denied
  router.navigate(['/access-denied']);
  return false;
};
